import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { addSeconds, isAfter } from "date-fns";

import { WatchedDate } from "@/stores/listeners/timeline-listeners/create-date-listener";
import { getCurrentDate } from "@/utils/datetime";

/**
 * A date information object for Socratic dialogue timing.
 */
export type SocraticState = {
  /**
   * Timing state for the current Dialogue
   * On reattempt, this is reset for the new Dialogue
   */
  dialogueTimeline: {
    /**
     * ID of this dialogue.
     */
    dialogueId: string | null;
    /**
     * Date when the dialogue was started.
     */
    dialogueStartDate: WatchedDate;
    /**
     * Date when the preparation time ends.
     * Student has preparation time from dialogue.startedAt + preparationTime.
     * If student starts recording early, this becomes the actual recording start time.
     */
    preparationEndDate: WatchedDate;
    /**
     * Date when the response/recording time ends.
     * This is calculated from when recording actually starts (either at preparation end or early start)
     * + responseTime.
     */
    responseEndDate: WatchedDate;
    /**
     * Whether the student has started recording.
     * This can happen during preparation time (early start) or after preparation time ends.
     */
    isRecording: boolean;
    /**
     * Whether recording has completed (either manually stopped or time expired).
     */
    recordingComplete: boolean;
    /**
     * The configured response time in seconds.
     * Stored so we can recalculate responseEndDate if student starts early.
     */
    responseTimeSeconds: number | null;
  };

  /**
   * Date when the reattempt window ends
   */
  reattemptWindowEndDate: WatchedDate;
  /**
   * Latest date/time a recording is allowed to start and still complete
   * before assessment auto-submission.
   */
  recordingStartCutoffDate: WatchedDate;
  /**
   * When true, the camera-required constraint is disabled and students may
   * turn off their camera. Will be driven by assessment settings in future.
   */
  cameraRequiredDisabled: boolean;
  /**
   * Whether a Socratic video upload is currently in progress.
   * Used to gate auto-submission navigation until the upload completes.
   */
  isUploading: boolean;
};

const mkDateState = (): WatchedDate => ({ date: null, passed: false });

const initialState: SocraticState = {
  dialogueTimeline: {
    dialogueId: null,
    dialogueStartDate: mkDateState(),
    preparationEndDate: mkDateState(),
    responseEndDate: mkDateState(),
    isRecording: false,
    recordingComplete: false,
    responseTimeSeconds: null,
  },
  reattemptWindowEndDate: mkDateState(),
  recordingStartCutoffDate: mkDateState(),
  cameraRequiredDisabled: false,
  isUploading: false,
};

export const socraticSlice = createSlice({
  name: "socratic",
  initialState,
  reducers: {
    /**
     * Initialise the Socratic Dialogue timing state.
     */
    updateSocraticDialogueTimeline: (
      { dialogueTimeline },
      action: PayloadAction<{
        dialogueId: string;
        startedAt: string;
        preparationTime: number; // in seconds
        responseTime: number; // in seconds
        recordingCompletedAt?: string; // optional timestamp when recording was completed (for resuming)
      }>
    ) => {
      const {
        dialogueId,
        startedAt,
        preparationTime,
        responseTime,
        recordingCompletedAt,
      } = action.payload;
      const now = getCurrentDate();
      const startDate = new Date(startedAt);

      dialogueTimeline.dialogueId = dialogueId;
      dialogueTimeline.responseTimeSeconds = responseTime;

      // Dialogue start date
      setSocraticDate(dialogueTimeline.dialogueStartDate, startDate, now);

      // Preparation end date
      const preparationEndDate = addSeconds(startDate, preparationTime);
      setSocraticDate(
        dialogueTimeline.preparationEndDate,
        preparationEndDate,
        now
      );

      // Initial response end date (assuming recording starts after preparation)
      // This will be recalculated if student starts recording early
      const responseEndDate = addSeconds(preparationEndDate, responseTime);
      setSocraticDate(dialogueTimeline.responseEndDate, responseEndDate, now);

      // Reset recording state
      dialogueTimeline.isRecording = false;
      dialogueTimeline.recordingComplete =
        recordingCompletedAt != null ? true : false;
    },

    /**
     * Mark that preparation time has passed.
     * If recording hasn't started yet, automatically start recording now.
     */
    preparationEndDatePassed: ({ dialogueTimeline }) => {
      dialogueTimeline.preparationEndDate.passed = true;

      // If not already recording, automatically start recording now
      if (
        !dialogueTimeline.isRecording &&
        !dialogueTimeline.recordingComplete
      ) {
        dialogueTimeline.isRecording = true;

        // Recalculate response end date from now
        if (dialogueTimeline.responseTimeSeconds !== null) {
          const now = getCurrentDate();
          const responseEndDate = addSeconds(
            now,
            dialogueTimeline.responseTimeSeconds
          );
          setSocraticDate(
            dialogueTimeline.responseEndDate,
            responseEndDate,
            now
          );
        }
      }
    },

    /**
     * Mark that response time has passed.
     * Recording automatically stops.
     */
    responseEndDatePassed: ({ dialogueTimeline }) => {
      dialogueTimeline.responseEndDate.passed = true;
      dialogueTimeline.isRecording = false;
      dialogueTimeline.recordingComplete = true;
    },

    /**
     * Student has chosen to start recording early (during preparation time).
     * Recalculates response end date from the early start time.
     * Marks preparation time as passed since we're skipping the rest of it.
     */
    startRecordingEarly: ({ dialogueTimeline }) => {
      if (
        !dialogueTimeline.isRecording &&
        !dialogueTimeline.recordingComplete
      ) {
        dialogueTimeline.isRecording = true;

        const now = getCurrentDate();

        setSocraticDate(dialogueTimeline.preparationEndDate, now, now); // Mark preparation end as now (passed)

        // Recalculate response end date from now (early start)
        if (dialogueTimeline.responseTimeSeconds !== null) {
          const responseEndDate = addSeconds(
            now,
            dialogueTimeline.responseTimeSeconds
          );
          setSocraticDate(
            dialogueTimeline.responseEndDate,
            responseEndDate,
            now
          );
        }
      }
    },

    /**
     * Student has chosen to stop recording early (before response time ends).
     * Marks response time as passed since we're done recording.
     */
    stopRecordingEarly: ({ dialogueTimeline }) => {
      if (dialogueTimeline.isRecording) {
        dialogueTimeline.isRecording = false;
        dialogueTimeline.recordingComplete = true;

        const now = getCurrentDate();

        setSocraticDate(dialogueTimeline.responseEndDate, now, now);
      }
    },

    /**
     * Reset the Socratic dialogue state.
     */
    resetSocraticDialogue: (state) => {
      state.dialogueTimeline = initialState.dialogueTimeline;
    },

    /**
     * Initialise the Socratic Session timing state.
     */
    updateSocraticSessionTimeline: (
      state,
      action: PayloadAction<{
        reattemptWindowEndsAt: string | null;
        recordingStartCutoffAt: string | null;
      }>
    ) => {
      const now = getCurrentDate();

      if (action.payload.reattemptWindowEndsAt != null) {
        setSocraticDate(
          state.reattemptWindowEndDate,
          action.payload.reattemptWindowEndsAt,
          now
        );
      } else {
        state.reattemptWindowEndDate = mkDateState();
      }

      if (action.payload.recordingStartCutoffAt != null) {
        setSocraticDate(
          state.recordingStartCutoffDate,
          action.payload.recordingStartCutoffAt,
          now
        );
      } else {
        state.recordingStartCutoffDate = mkDateState();
      }
    },

    /**
     * Mark that the reattempt window has passed.
     */
    reattemptWindowEndDatePassed: (state) => {
      state.reattemptWindowEndDate.passed = true;
    },

    /**
     * Mark that the recording start cutoff has passed.
     */
    recordingStartCutoffDatePassed: (state) => {
      state.recordingStartCutoffDate.passed = true;
    },

    setCameraRequiredDisabled: (state, action: PayloadAction<boolean>) => {
      state.cameraRequiredDisabled = action.payload;
    },

    setIsUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload;
    },
  },
});

function setSocraticDate(
  draft: Draft<WatchedDate>,
  value: string | Date | null,
  now: Date
) {
  if (value != null) {
    const date = new Date(value);
    draft.date = date.toISOString();
    draft.passed = isAfter(now, date);
  } else {
    draft.date = null;
    draft.passed = false;
  }
}

export const {
  updateSocraticDialogueTimeline,
  preparationEndDatePassed,
  responseEndDatePassed,
  startRecordingEarly,
  stopRecordingEarly,
  resetSocraticDialogue,
  updateSocraticSessionTimeline,
  reattemptWindowEndDatePassed,
  recordingStartCutoffDatePassed,
  setCameraRequiredDisabled,
  setIsUploading,
} = socraticSlice.actions;

export const socraticReducer = socraticSlice.reducer;
